/**
 * Creating a crew, in one go.
 *
 * Jonathon, 2026-09-14: "I don't want it to say new crew. I want it to be a pop
 * up where we enter all of the crew's info and then submit it." What it said
 * before was worse than untidy — Create wrote a row called "New crew", and
 * because crew names are unique, one left unrenamed meant the next Create
 * failed with `duplicate key value violates unique constraint "crews_name_key"`
 * and no crew at all.
 *
 * So the name is typed before anything is written, and the collision is caught
 * here, in a sentence naming the crew that is in the way.
 *
 * Insurance and paperwork are deliberately not on this form. They come off a
 * certificate — uploading one on the Documents screen fills those fields in and
 * shows the reading for checking first — and typing them from memory at the
 * moment a crew is added is how a job gets scheduled against cover nobody has
 * seen.
 */

import {
  blankCrewDraft,
  crewDraftProblem,
  formatExpiryDate,
  formatPhoneInput,
  type Coverage,
  type CrewDraft,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal, Row } from "../../components/ui";
import { t } from "../../i18n";
import { createCrew } from "../../lib/crews";

/**
 * How a field of a crew's papers is named on the documents screen's review
 * list. Here beside the form that makes a crew, so both crews screens can
 * read it without importing each other.
 */
export const CREW_FIELD_LABELS: Record<string, string> = {
  document_kind: "Document",
  has_workers_comp: "Workers' comp",
  workers_comp_expires_on: "Workers' comp expires",
  has_general_liability: "General liability",
  general_liability_expires_on: "General liability expires",
  insurer: "Insurer",
  legal_name: "Legal name",
  tax_id_type: "Tax ID type",
  notes: "Notes",
};

/**
 * A crew's cover in a sentence, in the app's language.
 *
 * The judgement is `crewCoverage` in packages/core; this only says it. Each
 * sentence is the one `coverageLabel` gives in English, so the English
 * screen reads exactly as it did.
 */
export function coverageText(coverage: Coverage): string {
  switch (coverage.state) {
    case "expired":
      return coverage.days === -1
        ? t("Expired yesterday")
        : t("Expired {n} days ago", { n: Math.abs(coverage.days ?? 0) });
    case "expiring":
      return coverage.days === 0
        ? t("Expires today")
        : coverage.days === 1
          ? t("Expires tomorrow")
          : t("Expires in {n} days", { n: coverage.days ?? 0 });
    case "uncovered":
      return t("Not covered");
    case "undated":
      return t("No expiry on file");
    case "unknown":
      return t("Not recorded");
    case "covered":
      return coverage.expiresOn
        ? t("Covered to {date}", {
            date: formatExpiryDate(coverage.expiresOn),
          })
        : t("Covered");
  }
}

export function NewCrewModal({
  existingNames,
  trades,
  onClose,
  onCreated,
}: {
  /** Every crew name in the table, working or not. */
  existingNames: string[];
  trades: { id: string; name: string }[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<CrewDraft>(blankCrewDraft);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (patch: Partial<CrewDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const toggleTrade = (id: string) =>
    set({
      tradeIds: draft.tradeIds.includes(id)
        ? draft.tradeIds.filter((t) => t !== id)
        : [...draft.tradeIds, id],
    });

  const submit = () => {
    const problem = crewDraftProblem(draft, existingNames);
    if (problem) {
      setError(t(problem));
      return;
    }

    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await createCrew(draft);
        await onCreated();
        onClose();
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : String(caught));
        setBusy(false);
      }
    })();
  };

  return (
    <Modal
      title={t("New crew")}
      subtitle={t(
        "Insurance and documents are added afterwards, from the crew's Documents button.",
      )}
      onClose={onClose}
      roomy
      actions={
        <>
          <button className="btn-small" onClick={onClose} disabled={busy}>
            {t("Cancel")}
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? t("Creating…") : t("Create crew")}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />

      <Row label={t("Company")}>
        <input
          aria-label={t("Company")}
          value={draft.name}
          autoFocus
          disabled={busy}
          placeholder={t("The company — or the person, if it is just them")}
          onChange={(e) => set({ name: e.target.value })}
        />
      </Row>
      <Row label={t("Contact")}>
        <input
          aria-label={t("Contact")}
          value={draft.contact_name}
          disabled={busy}
          // Shown in brackets after the company everywhere a crew is named:
          // "Apex Metal (Brad Kiley)". Optional, because a one-man crew is
          // already named.
          placeholder={t("Who to call there")}
          onChange={(e) => set({ contact_name: e.target.value })}
        />
      </Row>
      <Row label={t("Phone")}>
        <input
          aria-label={t("Phone")}
          value={draft.phone}
          disabled={busy}
          // Punctuated as it is typed, the same as every other number in the
          // app, so it can be read back off a screen in a driveway.
          onChange={(e) => set({ phone: formatPhoneInput(e.target.value) })}
        />
      </Row>
      <Row label={t("Email")}>
        <input
          aria-label={t("Email")}
          type="email"
          value={draft.email}
          disabled={busy}
          onChange={(e) => set({ email: e.target.value })}
        />
      </Row>

      <div className="chk-sub">{t("Trades")}</div>
      {trades.length === 0 ? (
        <p className="na-hint">{t("No trades set up yet.")}</p>
      ) : (
        <div className="crewtrades">
          {trades.map((trade) => (
            <label className="inline" key={trade.id}>
              <input
                type="checkbox"
                checked={draft.tradeIds.includes(trade.id)}
                disabled={busy}
                onChange={() => toggleTrade(trade.id)}
              />
              {trade.name}
            </label>
          ))}
        </div>
      )}

      <div className="chk-sub">{t("Settings")}</div>
      <Row label={t("Active")}>
        <label className="inline">
          <input
            type="checkbox"
            aria-label={t("Active")}
            checked={draft.active}
            disabled={busy}
            onChange={(e) => set({ active: e.target.checked })}
          />
          {t("Working for us now")}
        </label>
      </Row>
      <Row label={t("Can login")}>
        <label className="inline">
          <input
            type="checkbox"
            aria-label={t("Can login")}
            checked={draft.can_login}
            disabled={busy}
            onChange={(e) => set({ can_login: e.target.checked })}
          />
          {t("Signs in to see their own work orders")}
        </label>
      </Row>
      <Row label={t("In house")}>
        <label className="inline">
          <input
            type="checkbox"
            aria-label={t("In house")}
            checked={draft.is_in_house}
            disabled={busy}
            onChange={(e) => set({ is_in_house: e.target.checked })}
          />
          {t("Our own employee — costed, never paid out")}
        </label>
      </Row>
    </Modal>
  );
}
